import { useEffect, useState, useRef } from "react";
import "./RolePermissions.css";
import { getRolePermissions, updateRolePermissions } from "../../services/wisdomServices/rolePermissionService";
import { AdminUserSettingsHeaderLink } from "../../components/adminSettingsHeaderLinks/AdminUserSettingsHeaderLink";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";

const TABLE_RESERVED_HEIGHT = 380;
const TABLE_HEADER_HEIGHT = 90;
const APPROXIMATE_ROW_HEIGHT = 56;
const MIN_TABLE_HEIGHT = 500;
const MIN_ROWS = 7;
const MAX_ROWS = 50;

const calculateTableLayout = () => {
    if (typeof window === "undefined") {
        return {
            rows: MIN_ROWS,
            scrollHeight: `${MIN_TABLE_HEIGHT}px`,
        };
    }

    const viewportHeight =
        window.visualViewport?.height || window.innerHeight;

    const scrollHeight = Math.max(
        MIN_TABLE_HEIGHT,
        viewportHeight - TABLE_RESERVED_HEIGHT
    );

    const rows = Math.min(
        MAX_ROWS,
        Math.max(
            MIN_ROWS,
            Math.floor(
                (scrollHeight - TABLE_HEADER_HEIGHT) /
                    APPROXIMATE_ROW_HEIGHT
            )
        )
    );

    return {
        rows,
        scrollHeight: `${scrollHeight}px`,
    };
};

export function RolePermissions() {
    const REQUEST_INTERVAL_MS = 1500;
    const requestInFlightRef = useRef(false);
    const lastRequestAtRef = useRef(0);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [loadingRolePermissions, setLoadingRolePermissions] = useState(false);
    const [tableLayout, setTableLayout] = useState(calculateTableLayout);

    useEffect(() => {
        listRolePermissions();
    }, []);

    useEffect(() => {
        const updateTableLayout = () => {
            setTableLayout(calculateTableLayout());
        };

        window.addEventListener("resize", updateTableLayout);
        window.visualViewport?.addEventListener(
            "resize",
            updateTableLayout
        );

        return () => {
            window.removeEventListener("resize", updateTableLayout);
            window.visualViewport?.removeEventListener(
                "resize",
                updateTableLayout
            );
        };
    }, []);

    const listRolePermissions = async () => {
        try {
            setLoadingRolePermissions(true);

            const response = await getRolePermissions();
            setRolePermissions(response.data || []);
        } catch (error) {
            console.error("Something went wrong: ", error);
        } finally {
            setLoadingRolePermissions(false);
        }
    };

    const roleTypes = [
        { key: "applicant", label: "Applicant" },
        { key: "customer", label: "Customer" },
        { key: "agent", label: "User" },
        { key: "team_leader", label: "Team Leader" },
        { key: "manager", label: "Manager" },
        { key: "recruiter", label: "Recruiter" },
        { key: "csm", label: "CSM" },
        { key: "hr", label: "HR" },
        { key: "vp", label: "VP" },
        { key: "ceo", label: "CEO" },
    ];

    const permissionTypes = [
        {
            key: "none",
            label: "✖",
            tooltip: "No access",
            apiValue: null,
        },
        {
            key: "own",
            label: "✔*",
            tooltip: "Own records only",
            apiValue: "ownership",
        },
        {
            key: "team",
            label: "✔†",
            tooltip: "Scoped to own team / managed projects",
            apiValue: "project-scoped",
        },
        {
            key: "full",
            label: "✔",
            tooltip: "Full access",
            apiValue: "full",
        },
    ];

    const handlePermissionChange = async (
        rowData,
        role,
        permission
    ) => {
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestAtRef.current;

        if (
            requestInFlightRef.current ||
            timeSinceLastRequest < REQUEST_INTERVAL_MS
        ) {
            return;
        }

        const previousGrants = { ...rowData.grants };

        const updatedGrants = {
            ...rowData.grants,
            [role]: permission,
        };

        const payload = {
            endpoint: rowData.endpoint,
            method: rowData.method,
            grants: updatedGrants,
        };

        requestInFlightRef.current = true;
        lastRequestAtRef.current = now;

        // Atualização otimista
        setRolePermissions((currentPermissions) =>
            currentPermissions.map((item) => {
                if (
                    item.endpoint !== rowData.endpoint ||
                    item.method !== rowData.method
                ) {
                    return item;
                }

                return {
                    ...item,
                    grants: updatedGrants,
                };
            })
        );

        try {
            await updateRolePermissions(payload);
        } catch (error) {
            console.error(
                "Error updating role permission:",
                error
            );

            // Restaura os valores anteriores se o PUT falhar
            setRolePermissions((currentPermissions) =>
                currentPermissions.map((item) => {
                    if (
                        item.endpoint !== rowData.endpoint ||
                        item.method !== rowData.method
                    ) {
                        return item;
                    }

                    return {
                        ...item,
                        grants: previousGrants,
                    };
                })
            );
        } finally {
            requestInFlightRef.current = false;
        }
    };

    const confirmPermissionChange = (
        rowData,
        role,
        permission
    ) => {
        const roleLabel =
            roleTypes.find((roleType) => roleType.key === role)?.label || role;

        const permissionLabel =
            permissionTypes.find(
                (permissionType) => permissionType.apiValue === permission
            )?.tooltip || "No access";

        confirmDialog({
            header: "Confirm permission update",
            message: `Are you sure you want to change ${roleLabel} permission to "${permissionLabel}" for ${rowData.method} ${rowData.endpoint}?`,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Update",
            rejectLabel: "Cancel",
            accept: () =>
                handlePermissionChange(
                    rowData,
                    role,
                    permission
                ),
        });
    };

    const renderPermission = (role, permission) => {
        return (rowData) => {
            const currentPermission =
                rowData?.grants?.[role] ?? null;

            const isChecked =
                currentPermission === permission;

            return (
                <Checkbox
                    checked={isChecked}
                    onChange={(event) => {
                        if (!event.checked) {
                            return;
                        }

                        confirmPermissionChange(
                            rowData,
                            role,
                            permission
                        );
                    }}
                />
            );
        };
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column
                    header="Endpoint"
                    rowSpan={2}
                    frozen
                    alignFrozen="left"
                    
                    className="endpointHeader"
                />

                <Column
                    header="Method"
                    rowSpan={2}
                    frozen
                    alignFrozen="left"
                    className="methodHeader"
                />

                {roleTypes.map((role) => (
                    <Column
                        key={role.key}
                        header={role.label}
                        colSpan={permissionTypes.length}
                        className="roleHeader"
                    />
                ))}
            </Row>

            <Row>
                {roleTypes.map((role) =>
                    permissionTypes.map((permission, index) => (
                        <Column
                            key={`${role.key}-${permission.key}`}
                            header={permission.label}
                            title={permission.tooltip}
                            className={
                                index === permissionTypes.length - 1
                                    ? "permissionHeader roleSeparator"
                                    : "permissionHeader"
                            }
                        />
                    ))
                )}
            </Row>
        </ColumnGroup>
    );

    const tableHeader = (
        <div className="permissionsLegend">
            <span>
                <strong>✖</strong> No access
            </span>
            <span>
                <strong>✔*</strong> Own records only
            </span>
            <span>
                <strong>✔†</strong> Scoped to own team / managed projects
            </span>
            <span>
                <strong>✔</strong> Full access
            </span>
        </div>
    );

    const endpointTemplate = (rowData) => {
        const endpoint =
            rowData.endpoint.split("api/v1/")[1] ?? rowData.endpoint;

        return (
            <div className="endpointCell">
                <span className="endpointText">
                    {endpoint}
                </span>
            </div>
        );
    };

    const getMethodClassName = (method) => {
        switch (method) {
            case "GET":
                return "READ";
            case "POST":
                return "CREATE";
            case "PUT":
                return "UPDATE";
            case "PATCH":
                return "UPDATE";
            case "DELETE":
                return "DELETE";
            default:
                return "";
        }
    }
    const methodTemplate = (rowData) => {
        return (
            <div className="methodCell">
                <span className="methodText">
                    {getMethodClassName(rowData.method)}
                </span>
            </div>
        );
    };

    return (
        <div className="settingsContainer">
            <AdminUserSettingsHeaderLink />

            <Box sx={{ flexGrow: 1, minWidth: 0 }} className="settingsBody">
                <Grid container>
                    <Grid size={{ xs: 11 }}>
                        <h3 className="settingsLabel">Role Permissions</h3>
                    </Grid>

                    <Grid size={{ xs: 11 }} className="update-fields">
                        <div className="rolePermissionsTableWrapper">
                            <DataTable
                                value={rolePermissions}
                                loading={loadingRolePermissions}
                                scrollable
                                scrollDirection="both"
                                scrollHeight={tableLayout.scrollHeight}
                                paginator
                                rows={tableLayout.rows}
                                header={tableHeader}
                                headerColumnGroup={headerGroup}
                                tableStyle={{ minWidth: "1940px" }}
                                className="rolePermissionsTable"
                            >
                                <Column
                                    body={endpointTemplate}
                                    frozen
                                    alignFrozen="left"
                                    className="endpointColumn"
                                />

                                <Column
                                    body={methodTemplate}
                                    frozen
                                    alignFrozen="left"
                                    className="methodColumn"
                                />

                                {roleTypes.map((role) =>
                                    permissionTypes.map((permission, index) => (
                                        <Column
                                            key={`${role.key}-${permission.key}`}
                                            className={
                                                index === permissionTypes.length - 1
                                                    ? "permissionColumn roleSeparator"
                                                    : "permissionColumn"
                                            }
                                            body={renderPermission(
                                                role.key,
                                                permission.apiValue
                                            )}
                                        />
                                    ))
                                )}
                            </DataTable>
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
}

export default RolePermissions;